import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  Stack,
  CircularProgress,
  CardMedia,
} from "@mui/material";
import { CustomModal } from "~components/atoms";
import { Product as IProduct } from "~api/stream/interfaces";
import { getStyles as getModalStyles } from "~components/atoms/CustomModal/styles";
import { AddIcon } from "~components/Icons";
import { authFetch } from "~helpers/authFetch";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import { useStreamContext } from "~contexts/Stream";

interface ProductDetailModalProps {
  isOpened: boolean;
  onClose: () => void;
  product: IProduct | null;
  categoryId: number | null;
  onProductAdded: (product: any) => void;
}

const ProductDetailModal = ({
  isOpened,
  onClose,
  product,
  categoryId,
  onProductAdded,
}: ProductDetailModalProps) => {
  const theme: any = useTheme();
  const { modal: modalStyles } = getModalStyles(theme);
  const { setToastData } = useToastNotificationContext();
  const { currentStream } = useStreamContext();
  const [isAdding, setIsAdding] = useState(false);

  const handleAddProduct = async () => {
    if (!product || !categoryId || !currentStream) {
      return;
    }

    setIsAdding(true);
    try {
      const productId = product.provider?.productId || product.id;

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/ecommerce/products/add-to-category`,
        method: "POST",
        data: {
          productId: String(productId),
          categoryId,
        },
      });

      const insertedProduct = await response.json();

      setToastData(
        {
          title: "produto adicionado",
          text: "Produto adicionado à categoria com sucesso",
        },
        "success"
      );

      onProductAdded(insertedProduct);
      onClose();
    } catch (error: any) {
      console.error("Error adding product:", error);
      setToastData(
        {
          title: "erro ao adicionar",
          text: error?.message || "Erro ao adicionar produto à categoria",
        },
        "error"
      );
    } finally {
      setIsAdding(false);
    }
  };

  if (!product) {
    return null;
  }

  const productImage =
    product.images?.thumbs?.[0] ||
    product.images?.std?.[0] ||
    product.images?.hd?.[0] ||
    "";
  const productPrice = product.items?.[0]?.price
    ? `R$ ${product.items[0].price.toFixed(2)}`
    : product.price
    ? `R$ ${product.price.toFixed(2)}`
    : "Preço não disponível";

  return (
    <CustomModal isOpened={isOpened} onClose={onClose}>
      <Stack spacing={3}>
        <Typography style={modalStyles.titleStyle}>
          {product.productName || "Produto"}
        </Typography>

        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: theme.palette.grey[800],
          }}
        >
          {productImage && (
            <CardMedia
              component="img"
              image={productImage}
              alt={product.productName}
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "300px",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}

          {/* Botão de adicionar sobre a imagem */}
          <Box
            sx={{
              position: "absolute",
              top: "16px",
              right: "16px",
              zIndex: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={handleAddProduct}
              disabled={isAdding || !categoryId}
              startIcon={
                isAdding ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  <AddIcon />
                )
              }
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: "white",
                borderRadius: "8px",
                padding: "8px 16px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&:disabled": {
                  backgroundColor: theme.palette.grey[600],
                  color: theme.palette.grey[400],
                },
              }}
            >
              {isAdding ? "Adicionando..." : "Adicionar Produto"}
            </Button>
          </Box>
        </Box>

        <Stack spacing={2}>
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: theme.palette.grey[400],
                mb: 0.5,
              }}
            >
              ID do Produto
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                color: theme.palette.grey[200],
                fontFamily: "monospace",
              }}
            >
              {product.provider?.productId || product.id || "N/A"}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: theme.palette.grey[400],
                mb: 0.5,
              }}
            >
              Preço
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: theme.palette.grey[100],
                fontWeight: 600,
              }}
            >
              {productPrice}
            </Typography>
          </Box>

          {product.description && product.description.length > 0 && (
            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.grey[400],
                  mb: 0.5,
                }}
              >
                Descrição
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.grey[300],
                  lineHeight: 1.6,
                }}
              >
                {typeof product.description[0] === "string"
                  ? product.description[0]
                  : product.description[0]?.content || "Sem descrição disponível"}
              </Typography>
            </Box>
          )}
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.grey[700]}`,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: theme.palette.grey[400],
              "&:hover": {
                backgroundColor: theme.palette.grey[800],
              },
            }}
          >
            Fechar
          </Button>
        </Box>
      </Stack>
    </CustomModal>
  );
};

export default ProductDetailModal;
